import type {
  TryOnResponse,
  TryOnStatus,
  FitAreaInfo,
  SavedTryOnResultResponse,
  SizingResult,
} from "./types";
import { getOrCreateSessionId, getDeviceHint } from "./session";

const DEFAULT_API_URL = "http://localhost:4000";

export type StyleMatchSlot =
  | "top"
  | "bottom"
  | "dress"
  | "outerwear"
  | "shoe"
  | "bag"
  | "accessory";

export interface StyleMatchSelection {
  slot?: StyleMatchSlot | string;
  productId?: string;
  title: string;
  image: string;
  /** Clean transparent cutout prepared for both presentation and virtual
   * try-on. When present, the SDK promotes it over the original product image. */
  displayImage?: string;
  url?: string;
  color?: string;
  garmentType?: string;
  recommendedSize?: string;
}

export interface TryOnContext {
  /** Storefront product the customer is currently viewing — shown in analytics. */
  productId?: string;
  productTitle?: string;
  productCategory?: string;
  productSubcategory?: string;
  productFitType?: string;
  productType?: string;
  productTags?: string[];
  /** Free-text product description forwarded to the try-on prompt so Gemini
   *  knows the garment context (e.g. "Brown slip maxi dress, spaghetti straps"). */
  productDescription?: string;
  /** Material / fabric composition (e.g. "100% Cotton", "60% Polyester / 40% Polyester")
   *  — forwarded to the prompt so Gemini drapes the fabric correctly without
   *  having to guess from the image alone. */
  productMaterial?: string;
  /** Optional clean product/detail image. The backend sends it to Gemini as an
   *  additional design reference while `garmentImage` remains the worn/model
   *  styling reference. */
  garmentDetailImage?: string;
  /** Optional silhouette-prompt context forwarded to the backend Gemini try-on
   *  call. Enables doc-example-grade fit reasoning ("X tension at button
   *  because waist 54.4 exceeds size 50 drop"). All fields optional. */
  silhouetteContext?: {
    recommendedSize?: string;
    recommendedSizeMeasurements?: string;
    /** @deprecated Full size guides are no longer forwarded to try-on prompts. */
    sizeChartSummary?: string;
    userMeasurementsText?: string;
    userHeight?: string;
    userWeight?: string;
  };
  /** True when `modelImage` is a previous try-on result rather than the
   *  user's raw photo. Switches the backend to an edit-mode prompt that
   *  preserves accessories (socks, ties, pocket squares, cufflinks, shoes)
   *  the prior call already painted, instead of re-deriving them. */
  editFromPrevious?: boolean;
  /** Backend-cached photo id from a previous /tryon response. When provided
   *  the SDK may pass an empty `modelImage` (or omit it) and the backend
   *  will use the cached photo. Saves uploading 700–900 KB on every regen
   *  AND guarantees byte-identical photo across regens (no JPEG-recompression
   *  drift). On cache miss the SDK should fall back to sending the full image. */
  modelImageId?: string;
  /** Per-modal-open id used to find the exact SDK interaction in Clarity. */
  sdkOpenId?: string;
  profileLoggedIn?: boolean;
  profileId?: string;
  profileName?: string;
  sizingResult?: SizingResult | null;
  styleMatchSelections?: StyleMatchSelection[];
}

export type TryOnCategory =
  | "apparel"
  | "shoe"
  | "bag"
  | "hat"
  | "sunglasses"
  | "earring"
  | "necklace"
  | "bracelet"
  | "ring"
  | "belt"
  | "watch"
  | "accessory";

export interface TryOnFeedbackInput {
  jobId?: string;
  historyEntryId?: string;
  rating?: number;
  note?: string;
  productId?: string;
  productTitle?: string;
  productUrl?: string;
  recommendedSize?: string;
  profileLoggedIn?: boolean;
  profileId?: string;
  profileName?: string;
  profileAccessToken?: string;
}

export type SdkBehaviorEventType =
  | "PRODUCT_VIEW"
  | "SDK_OPENED"
  | "PHOTO_UPLOADED"
  | "SIZING_STARTED"
  | "SIZING_FAILED"
  | "SIZE_RECOMMENDATION_SHOWN"
  | "SIZE_RECOMMENDATION_ACCEPTED"
  | "ADD_TO_CART_FROM_TRYON"
  | "SDK_CLIENT_ERROR"
  | "SDK_PROFILE_CREATED"
  | "SDK_PROFILE_UPDATED"
  | "SDK_PROFILE_SELECTED";

export interface SdkBehaviorEventInput {
  eventType: SdkBehaviorEventType;
  sdkOpenId?: string;
  productId?: string;
  productTitle?: string;
  productUrl?: string;
  jobId?: string;
  recommendedSize?: string;
  profileLoggedIn?: boolean;
  profileId?: string;
  profileName?: string;
  profileOrigin?: string;
  metadata?: Record<string, unknown>;
}

export interface SdkClientErrorInput {
  message: string;
  code?: string;
  stack?: string;
  component?: string;
  view?: string;
  severity?: "low" | "medium" | "high" | "critical";
  productId?: string;
  productTitle?: string;
  productUrl?: string;
  jobId?: string;
  metadata?: Record<string, unknown>;
}

export interface StyleMatchRequestProduct {
  productId?: string;
  title?: string;
  image?: string;
  images?: string[];
  url?: string;
  category?: string;
  subcategory?: string;
  type?: string;
  fitType?: string;
  gender?: string;
  vendor?: string;
  tags?: string[];
  description?: string;
  material?: string;
  color?: string;
  colorHex?: string;
  variants?: unknown[];
}

export interface StyleMatchRequestProfile {
  gender?: string;
  recommendedSize?: string;
  measurements?: Record<string, unknown>;
}

export interface StyleMatchRequest {
  product: StyleMatchRequestProduct;
  profile?: StyleMatchRequestProfile;
  modelImageId?: string;
  personImage?: string;
  limitOutfits?: number;
  optionsPerSlot?: number;
}

export interface StyleMatchItem {
  slot: StyleMatchSlot;
  productId: string;
  title: string;
  image: string;
  url?: string;
  color: string;
  garmentType: string;
  recommendedSize: string;
  availability: "InStock" | "LowStock" | "OutOfStock";
  selected: boolean;
  score: number;
  alternatives: Array<Omit<StyleMatchItem, "alternatives" | "selected"> & { selected: false }>;
}

export interface StyleMatchOutfit {
  id: string;
  label: string;
  score: number;
  items: StyleMatchItem[];
}

export interface StyleMatchResponse {
  source: "rag" | "openai_rag" | "catalog-fallback" | "empty" | "disabled" | "person_image_required";
  enabled?: boolean;
  disabledReason?: "global_disabled" | "store_disabled";
  errorCode?: string;
  anchor: StyleMatchRequestProduct & { slot?: string | null };
  outfits: StyleMatchOutfit[];
}

export class ApiClient {
  private apiKey?: string;
  private baseUrl: string;

  constructor(apiKey?: string, apiUrl?: string) {
    this.apiKey = apiKey || undefined;
    this.baseUrl = (apiUrl || DEFAULT_API_URL).replace(/\/+$/, "");
  }

  private get headers(): Record<string, string> {
    return jsonHeaders(this.apiKey);
  }

  async submitTryOn(
    modelImage: string,
    garmentImage: string,
    fitInfo?: FitAreaInfo[],
    category?: TryOnCategory,
    context?: TryOnContext
  ): Promise<TryOnResponse> {
    const body: Record<string, unknown> = {
      garmentImage,
      // Attribution fields — backend logs these into the TryOnEvent collection
      // so non-technical operators can answer "which products are being tried on
      // and by how many customers". Safe fallbacks when absent.
      sessionId: getOrCreateSessionId(),
      deviceHint: getDeviceHint(),
    };
    // When we already have a backend-cached photo id, send only that — the
    // backend will reuse the same canonical bytes for every regen. modelImage
    // is still sent if present so the backend can fall back gracefully on a
    // cache miss (e.g. across PM2 worker boundaries or after the 1h TTL).
    if (context?.modelImageId) body.modelImageId = context.modelImageId;
    if (context?.sdkOpenId) body.sdkOpenId = context.sdkOpenId;
    if (modelImage) body.modelImage = modelImage;
    if (fitInfo && fitInfo.length > 0) body.fitInfo = fitInfo;
    if (category && category !== "apparel") body.category = category;
    if (context?.productId) body.productId = context.productId;
    if (context?.productTitle) body.productTitle = context.productTitle;
    if (context?.productCategory) body.productCategory = context.productCategory;
    if (context?.productSubcategory) body.productSubcategory = context.productSubcategory;
    if (context?.productFitType) body.productFitType = context.productFitType;
    if (context?.productType) body.productType = context.productType;
    if (context?.productTags?.length) body.productTags = context.productTags;
    if (context?.productDescription) body.productDescription = context.productDescription;
    if (context?.productMaterial) body.productMaterial = context.productMaterial;
    if (context?.garmentDetailImage && context.garmentDetailImage !== garmentImage) body.garmentDetailImage = context.garmentDetailImage;
    if (context?.silhouetteContext) body.silhouetteContext = context.silhouetteContext;
    if (context?.editFromPrevious) body.editFromPrevious = true;
    if (context?.profileLoggedIn !== undefined) body.profileLoggedIn = context.profileLoggedIn;
    if (context?.profileId) body.profileId = context.profileId;
    if (context?.profileName) body.profileName = context.profileName;
    if (context?.styleMatchSelections?.length) body.styleMatchSelections = context.styleMatchSelections;
    if (context?.sizingResult) {
      body.sizingResult = context.sizingResult;
      body.recommendedSize = context.sizingResult.recommendedSize;
    } else if (context?.silhouetteContext?.recommendedSize) {
      body.recommendedSize = context.silhouetteContext.recommendedSize;
    }
    console.log("[ps-sdk:api] POST /api/v1/tryon payload", {
      modelImageBytes: modelImage.length,
      garmentImageBytes: garmentImage.length,
      garmentDetailImageSet: !!body.garmentDetailImage,
      styleMatchSelections: context?.styleMatchSelections?.map((item) => ({
        slot: item.slot,
        title: item.title,
        imageSet: Boolean(item.image),
      })) || null,
      category: category || "apparel",
      fitInfo: fitInfo?.map(f => ({
        area: f.area,
        fit: f.fit,
        userValue: f.userValue,
        garmentRange: f.garmentRange,
      })) || null,
      silhouetteContext: context?.silhouetteContext || null,
    });
    // Single-glance table of what Gemini will see per body area. Makes it
    // trivial to eyeball "my inseam pick is 30, my body is 32.5 — is that
    // what actually left the browser?" without parsing the JSON above.
    if (fitInfo && fitInfo.length > 0) {
      console.log("[ps-sdk:api] Body vs Garment (what Gemini will see):");
      for (const f of fitInfo) {
        const parts = (f.garmentRange || "").replace(/[^\d.\-–]/g, " ").trim().split(/[\s\-–]+/).filter(Boolean).map(Number).filter(n => !isNaN(n));
        let gap = "";
        if (parts.length && f.userValue != null) {
          const mid = (Math.min(...parts) + Math.max(...parts)) / 2;
          const diff = Math.round((f.userValue - mid) * 10) / 10;
          gap = diff > 0 ? `garment ${diff} smaller` : diff < 0 ? `garment ${Math.abs(diff)} larger` : "matched";
        }
        console.log(`[ps-sdk:api]   ${f.area.padEnd(10)} body=${f.userValue ?? "?"} garment=${f.garmentRange ?? "?"} → ${gap}`);
      }
    }
    const res = await fetch(`${this.baseUrl}/api/v1/tryon`, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify(body),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      if (res.status === 402) {
        throw new PrimeStyleError(
          data.message || "Insufficient try-ons",
          "INSUFFICIENT_BALANCE"
        );
      }
      if (res.status === 429 && data.error === "TRYON_IP_LIMIT_REACHED") {
        throw new PrimeStyleError(
          data.message || "This product has already been tried on from this IP address.",
          "TRYON_IP_LIMIT_REACHED",
          data
        );
      }
      throw new PrimeStyleError(
        data.message || "Failed to submit try-on",
        "API_ERROR"
      );
    }

    return res.json();
  }

  async getSavedTryOnResult(input: { productId: string }): Promise<SavedTryOnResultResponse> {
    const productId = input.productId.trim();
    if (!productId) return { found: false };
    const params = new URLSearchParams({
      productId,
      sessionId: getOrCreateSessionId(),
    });
    const res = await fetch(`${this.baseUrl}/api/v1/tryon/saved-result?${params.toString()}`, {
      headers: this.headers,
    });
    if (!res.ok) return { found: false };
    return res.json();
  }

  async getStyleMatchAvailability(): Promise<{
    enabled: boolean;
    globalEnabled: boolean;
    storeEnabled: boolean;
    disabledReason: "global_disabled" | "store_disabled" | null;
  }> {
    const res = await fetch(`${this.baseUrl}/api/v1/style/availability`, {
      headers: this.headers,
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to check style matching availability",
        "STYLE_MATCH_FAILED",
        data
      );
    }

    return res.json();
  }

  async getStyleMatches(input: StyleMatchRequest): Promise<StyleMatchResponse> {
    const res = await fetch(`${this.baseUrl}/api/v1/style/match`, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify(input),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to get style matches",
        "STYLE_MATCH_FAILED",
        data
      );
    }

    return res.json();
  }

  async removeGarmentBackground(imageUrl: string): Promise<string> {
    const res = await fetch(`${this.baseUrl}/api/v1/style/garment-cutout`, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({ imageUrl }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to prepare garment image",
        "GARMENT_CUTOUT_FAILED",
        data
      );
    }

    const blob = await res.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || imageUrl));
      reader.onerror = () => reject(reader.error || new Error("Failed to read garment cutout"));
      reader.readAsDataURL(blob);
    });
  }

  async getStatus(jobId: string): Promise<TryOnStatus> {
    const pollUrl = `${this.baseUrl}/api/v1/tryon/status/${encodeURIComponent(jobId)}?ps_poll=${Date.now()}`;
    const res = await fetch(pollUrl, {
      cache: "no-store",
      headers: {
        ...this.headers,
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to get status",
        "API_ERROR"
      );
    }

    const data = await res.json();
    return {
      ...data,
      imageUrl: data.imageUrl ?? data.resultImageUrl ?? data.outputUrl ?? data.remoteOutputUrl ?? null,
      message: data.message ?? data.error ?? "",
    };
  }

  async submitTryOnFeedback(input: TryOnFeedbackInput): Promise<{ ok: true }> {
    const res = await fetch(`${this.baseUrl}/api/v1/tryon/feedback`, {
      method: "POST",
      headers: this.headers,
      body: JSON.stringify({
        ...input,
        sessionId: getOrCreateSessionId(),
        deviceHint: getDeviceHint(),
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to submit try-on feedback",
        "API_ERROR"
      );
    }

    return res.json();
  }

  async reportEvent(input: SdkBehaviorEventInput): Promise<{ ok: true }> {
    const res = await fetch(`${this.baseUrl}/api/v1/tryon/event`, {
      method: "POST",
      headers: this.headers,
      keepalive: true,
      body: JSON.stringify({
        ...input,
        sessionId: getOrCreateSessionId(),
        deviceHint: getDeviceHint(),
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to report SDK event",
        "EVENT_REPORT_FAILED"
      );
    }

    return res.json();
  }

  async reportClientError(input: SdkClientErrorInput): Promise<{ ok: true }> {
    const res = await fetch(`${this.baseUrl}/api/v1/tryon/client-error`, {
      method: "POST",
      headers: this.headers,
      keepalive: true,
      body: JSON.stringify({
        ...input,
        sessionId: getOrCreateSessionId(),
        deviceHint: getDeviceHint(),
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new PrimeStyleError(
        data.message || "Failed to report SDK client error",
        "CLIENT_ERROR_REPORT_FAILED"
      );
    }

    return res.json();
  }

  getStreamUrl(jobId?: string): string {
    const streamPath = jobId ? `/api/v1/tryon/stream/${encodeURIComponent(jobId)}` : "/api/v1/tryon/stream";
    const streamUrl = `${this.baseUrl}${streamPath}`;
    return this.apiKey ? `${streamUrl}?key=${encodeURIComponent(this.apiKey)}` : streamUrl;
  }
}

export function jsonHeaders(apiKey?: string): Record<string, string> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (apiKey) headers.Authorization = `Bearer ${apiKey}`;
  return headers;
}

export class PrimeStyleError extends Error {
  code: string;
  details?: unknown;
  constructor(message: string, code: string, details?: unknown) {
    super(message);
    this.name = "PrimeStyleError";
    this.code = code;
    this.details = details;
  }
}
